import { SetStateAction, Dispatch, createContext, useContext, useState, useMemo, useCallback } from 'react'

import parseISO from 'date-fns/parseISO'

import Quote from '@legacy/models/Quote'
import { QuoteKind } from '@legacy/types/Quote'

export type SetQuoteCallback = Dispatch<SetStateAction<Quote | null>>

type ContextType = {
  activeQuote: Quote | null
  quoteMode?: QuoteKind
  setQuoteMode: (kind: QuoteKind | undefined) => void
  setActiveQuote: SetQuoteCallback
  formParameters: any
}

type ProviderProps = {
  initialQuote: Quote | null
  children: React.ReactNode
}

const ActiveQuoteContext = createContext<ContextType | undefined>(undefined)

function useActiveQuote() {
  const context = useContext(ActiveQuoteContext)
  if (!context) {
    throw new Error(`useActiveQuote must be used within an ActiveQuoteProvider`)
  }
  return context
}

const ActiveQuoteProvider = ({ initialQuote, children }: ProviderProps) => {
  const [activeQuote, setQuote] = useState<Quote | null>(initialQuote)
  const [quoteMode, setQuoteMode] = useState<QuoteKind | undefined>()

  const formParameters = useMemo(() => {
    if (!activeQuote) return {}

    const params = activeQuote.parameters

    if (params.policyStartDate && typeof params.policyStartDate === 'string') {
      params.policyStartDate = parseISO(params.policyStartDate)
    }
    if (params.policyEndDate && typeof params.policyEndDate === 'string') {
      params.policyEndDate = parseISO(params.policyEndDate)
    }
    if (params.claimsHistoryClaimsList) {
      const list = params.claimsHistoryClaimsList as any[]
      list.forEach((claim: any, index: number) => {
        if (typeof claim.claimedOn === 'string') {
          // @ts-expect-error We know it's an array
          params.claimsHistoryClaimsList[index].claimedOn = parseISO(claim.claimedOn)
        }
      })
    }

    return params
  }, [activeQuote])

  const setActiveQuote = useCallback((quote: any) => {
    setQuote(quote)
    if (quote?.kind) {
      setQuoteMode(quote.kind)
    }
  }, [])

  const value: ContextType = { activeQuote, setActiveQuote, formParameters, quoteMode, setQuoteMode }

  return <ActiveQuoteContext.Provider value={value}>{children}</ActiveQuoteContext.Provider>
}

export { ActiveQuoteProvider, useActiveQuote }
